/**
 * 扫码登录·被扫端（任务 6f；protocol §3.7）。LoginPage 卡片内子视图
 * （非独立路由：刷新回登录表单重来，守卫零改动，与 2FA/忘记密码同口径）。
 *
 * 流程：generate → uqr 渲染 QrcodeUrl（动态 import，不进首屏）→ 2s 轮询 check
 * （整个项目唯一允许的轮询）→ confirmed 凭证已在 Worker 落库，守卫接管导航。
 *
 * 过期判定在**本地计时**（expiresIn 秒）：Redis 键没了之后 check 只会报业务错
 * （Worker 折成 pending），不会再返回 expired——旧版靠服务端状态对死码会永久
 * 轮询，这里不复刻。到点停轮显示遮罩，点「刷新二维码」重新 generate。
 *
 * 生命周期全在一个 useEffect 里（attempt 计数驱动重跑）：刷新/卸载走 cleanup
 * 置 cancelled + 清定时器，旧一轮的异步结果全部作废，无 ref 突变。
 */
import { useEffect, useState } from 'preact/hooks';
import styles from './login.module.scss';
import { appWorker } from '~/store/appWorker';
import { globalStore } from '~/store/global';
import { checkLoginQr, generateLoginQr } from '~/store/authActions';
import { BRAND_NAME } from '~/brand';

const POLL_INTERVAL_MS = 2000;

type Phase = 'loading' | 'ready' | 'expired' | 'error';

export function QrCodeLoginView(props: { onBack: () => void }) {
  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState<Phase>('loading');
  const [svg, setSvg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let expireTimer: ReturnType<typeof setTimeout> | undefined;
    const stopTimers = () => {
      if (pollTimer !== undefined) clearInterval(pollTimer);
      if (expireTimer !== undefined) clearTimeout(expireTimer);
    };

    setPhase('loading');
    setSvg('');
    setError('');

    void (async () => {
      const result = await generateLoginQr(appWorker());
      if (cancelled) return;
      if (!result.ok) {
        setError(result.message);
        setPhase('error');
        return;
      }
      // QR 编码器按需加载（uqr ~4KB gzip），失败按生成失败处理可重试
      let rendered: string;
      try {
        const { renderSVG } = await import('uqr');
        rendered = renderSVG(result.qr.qrcodeUrl, { border: 1 });
      } catch {
        if (!cancelled) {
          setError('二维码渲染失败，请重试');
          setPhase('error');
        }
        return;
      }
      if (cancelled) return;
      setSvg(rendered);
      setPhase('ready');

      expireTimer = setTimeout(() => {
        stopTimers();
        setPhase('expired');
      }, result.qr.expiresIn * 1000);

      let inFlight = false;
      pollTimer = setInterval(() => {
        if (inFlight) return;
        inFlight = true;
        checkLoginQr(appWorker(), globalStore(), result.qr.qrcodeId)
          .then((outcome) => {
            if (cancelled) return;
            // confirmed：auth 已写入，守卫跳走本组件随之卸载，这里只需停轮
            if (outcome === 'confirmed' || outcome === 'expired') {
              stopTimers();
              if (outcome === 'expired') setPhase('expired');
            }
          })
          .finally(() => {
            inFlight = false;
          });
      }, POLL_INTERVAL_MS);
    })();

    return () => {
      cancelled = true;
      stopTimers();
    };
  }, [attempt]);

  const refresh = () => setAttempt((n) => n + 1);

  return (
    <div className={styles.form} data-testid="qr-login-view">
      <h2 className={styles.viewTitle}>扫码登录</h2>
      <p className={styles.hint}>请使用 {BRAND_NAME} 手机端扫码确认登录</p>
      <div className={styles.qrBox}>
        {phase !== 'error' && svg !== '' && (
          <div
            className={styles.qrSvg}
            data-testid="qr-canvas"
            // uqr 生成的本地 SVG，无用户输入，注入安全
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        )}
        {phase === 'loading' && svg === '' && <p className={styles.hint}>正在生成二维码…</p>}
        {phase === 'expired' && (
          <div className={styles.qrOverlay} data-testid="qr-expired">
            <p>二维码已过期</p>
            <button type="button" className={styles.submit} data-testid="qr-refresh" onClick={refresh}>
              刷新二维码
            </button>
          </div>
        )}
        {phase === 'error' && (
          <div className={styles.qrOverlay} data-testid="qr-error">
            <p className={styles.error}>{error}</p>
            <button type="button" className={styles.submit} data-testid="qr-refresh" onClick={refresh}>
              重试
            </button>
          </div>
        )}
      </div>
      <button type="button" className={styles.linkButton} data-testid="qr-back" onClick={props.onBack}>
        返回账号登录
      </button>
    </div>
  );
}
